import React, { useState } from 'react';
import SEO from '../components/SEO';
import PageHero from '../components/PageHero';
import GooglePlayLogo, { PLAY_STORE_URL } from '../components/GooglePlayLogo';
import useFadeIn from '../hooks/useFadeIn';

const Contact: React.FC = () => {
  useFadeIn();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate form submission
    setTimeout(() => {
      setSubmitMessage('Thank you for your message! We will get back to you soon.');
      setFormData({ name: '', email: '', subject: '', message: '' });
      setIsSubmitting(false);
    }, 1000);
  };

  const faqs = [
    {
      question: 'How do I reset my password?',
      answer: 'You can reset your password directly from the mobile app login screen by tapping "Forgot Password" and following the instructions.'
    },
    {
      question: 'Can I sync data across devices?',
      answer: 'Yes! eTENANT is cloud-based, so your data automatically syncs across all your devices when you log in with the same account.'
    },
    {
      question: 'Is my data secure?',
      answer: 'Absolutely. We use enterprise-grade security measures to protect your data, including encryption and secure cloud storage.'
    },
    {
      question: 'Do you offer training?',
      answer: 'We provide comprehensive documentation and video tutorials. Premium subscribers also get access to one-on-one training sessions.'
    }
  ];

  return (
    <div>
      <SEO
        title="Contact Us"
        description="Get in touch with the eTENANT team. Email support@etenant.net for help with the app — we respond Monday to Friday, 9AM to 6PM Philippine Time."
        path="/contact"
        breadcrumb="Contact Us"
        faqs={faqs}
      />

      <PageHero
        crumb="Contact Us"
        eyebrow="Contact"
        title={<>We're Here <span>to Help</span></>}
        subtitle="Have questions about eTENANT? Need support? We'd love to hear from you."
      />

      {/* Contact info + form */}
      <section className="contact-v2">
        <div className="container">
          <div className="row g-4 g-xl-5">
            <div className="col-lg-5">
              <div className="contact-v2-info fade-in-up">
                <span className="features-v2-eyebrow hiw-eyebrow-left">Get in touch</span>
                <h2 className="features-v2-title text-start">
                  Talk to the <span>eTENANT Team</span>
                </h2>
                <p className="hiw-text">
                  Our support team is ready to assist you with any questions about the eTENANT property management platform.
                </p>

                <div className="contact-v2-cards">
                  <a href="mailto:support@etenant.net" className="contact-v2-card">
                    <span className="features-v2-icon"><i className="fas fa-envelope" aria-hidden="true"></i></span>
                    <span>
                      <span className="contact-v2-card-label">Email Support</span>
                      <span className="contact-v2-card-value">support@etenant.net</span>
                      <span className="contact-v2-card-note">We typically respond within 24 hours</span>
                    </span>
                  </a>

                  <div className="contact-v2-card">
                    <span className="features-v2-icon"><i className="fas fa-clock" aria-hidden="true"></i></span>
                    <span>
                      <span className="contact-v2-card-label">Support Hours</span>
                      <span className="contact-v2-card-value">Monday - Friday: 9:00 AM - 6:00 PM</span>
                      <span className="contact-v2-card-note">Philippine Standard Time (PST)</span>
                    </span>
                  </div>

                  <div className="contact-v2-card">
                    <span className="features-v2-icon"><i className="fas fa-mobile-alt" aria-hidden="true"></i></span>
                    <span>
                      <span className="contact-v2-card-label">Mobile App</span>
                      <span className="contact-v2-card-value">Download eTENANT from Google Play Store</span>
                      <a
                        href={PLAY_STORE_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="contact-v2-play"
                      >
                        <GooglePlayLogo />
                        Download Now
                      </a>
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-lg-7">
              <div className="contact-v2-form fade-in-up delay-1">
                <h2 className="contact-v2-form-title">Send us a Message</h2>
                <p className="contact-v2-form-sub">Fill out the form and we'll get back to you as soon as we can.</p>

                {submitMessage && (
                  <div className="contact-v2-success" role="status">
                    <i className="fas fa-check-circle" aria-hidden="true"></i>
                    {submitMessage}
                  </div>
                )}

                <form onSubmit={handleSubmit}>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label htmlFor="name" className="contact-v2-label">Full Name *</label>
                      <input
                        type="text"
                        className="contact-v2-input"
                        id="name"
                        name="name"
                        placeholder="Juan Dela Cruz"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                      />
                    </div>

                    <div className="col-md-6">
                      <label htmlFor="email" className="contact-v2-label">Email Address *</label>
                      <input
                        type="email"
                        className="contact-v2-input"
                        id="email"
                        name="email"
                        placeholder="you@example.com"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                      />
                    </div>

                    <div className="col-12">
                      <label htmlFor="subject" className="contact-v2-label">Subject *</label>
                      <input
                        type="text"
                        className="contact-v2-input"
                        id="subject"
                        name="subject"
                        placeholder="How can we help?"
                        value={formData.subject}
                        onChange={handleInputChange}
                        required
                      />
                    </div>

                    <div className="col-12">
                      <label htmlFor="message" className="contact-v2-label">Message *</label>
                      <textarea
                        className="contact-v2-input contact-v2-textarea"
                        id="message"
                        name="message"
                        rows={6}
                        placeholder="Tell us a bit more..."
                        value={formData.message}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </div>

                  <button type="submit" className="contact-v2-submit" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <>
                        <i className="fas fa-spinner fa-spin" aria-hidden="true"></i>
                        Sending...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-paper-plane" aria-hidden="true"></i>
                        Send Message
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="contact-v2-faq">
        <div className="container">
          <div className="section-v2-header fade-in-up">
            <span className="features-v2-eyebrow">FAQ</span>
            <h2 className="features-v2-title">
              Common <span>Questions</span>
            </h2>
            <p className="features-v2-subtitle">Find quick answers to frequently asked questions.</p>
          </div>

          <div className="contact-v2-faq-list">
            {faqs.map((faq, index) => (
              <details key={faq.question} className="contact-v2-faq-item fade-in-up" open={index === 0}>
                <summary>
                  {faq.question}
                  <i className="fas fa-plus" aria-hidden="true"></i>
                </summary>
                <p>{faq.answer}</p>
              </details>
            ))}
          </div>

          <p className="contact-v2-faq-more">
            Still have questions?
            <a href="mailto:support@etenant.net"> Contact our support team</a>
          </p>
        </div>
      </section>
    </div>
  );
};

export default Contact;
